import { Transition } from '@headlessui/react'
import { ContactId } from 'modules/contacts/types/contact-interface'
import React, { Fragment, useEffect, useMemo, useState, type JSX } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { deepCompare } from 'shared/utils/deepCompare'
import { CheckCircleSelectedIcon } from '@systemeio/ui/icons'

export type ListingSelectType = (caption?: string) => {
  renderListingSelect: (children: JSX.Element) => JSX.Element
  closeMenu: () => void
  selectedAll: boolean
  exceptionIds: Set<ContactId>
  selectedIds: Set<ContactId>
}

interface UseListingSelectInterface<T, R extends { id: number }> {
  filter: T
  listing?: DataWithPaginationInterface<R>
  count?: number
}

export const useListingSelect = <T, R extends { id: number }>({
  listing,
  count = 0,
  filter,
}: UseListingSelectInterface<T, R>) => {
  const { t } = useLocoTranslation()
  const [isMenuShown, setIsMenuShown] = useState(false)
  const [idsCount, setIdsCount] = useState<string | number>(0)
  const [allContactIds, setAllContactIds] = useState<Set<ContactId>>(new Set())
  const [selectedIdsState, setSelectedIdsState] = useState<Set<ContactId>>(new Set())
  const [exceptionIdsState, setExceptionIdsState] = useState<Set<ContactId>>(new Set())
  const [newFilter, setNewFilter] = useState<T>(filter)

  const [selectedAll, setSelectedAll] = useState(false)

  const selectedIds = useMemo(
    () => ({
      get: () => selectedIdsState,
      add: (id: number) => setSelectedIdsState(prev => new Set(prev.add(id))),
      remove: (id: number) =>
        setSelectedIdsState(prev => {
          prev.delete(id)
          return new Set(prev)
        }),
      clear: () => setSelectedIdsState(new Set()),
    }),
    [selectedIdsState],
  )

  useEffect(() => {
    if (!deepCompare(filter, newFilter)) {
      setNewFilter(filter)
      setSelectedIdsState(new Set())
      setSelectedAll(false)
    }
  }, [filter, newFilter])

  useEffect(() => {
    setIsMenuShown(selectedAll || selectedIdsState.size !== 0)
  }, [selectedAll, selectedIdsState.size])

  const exceptionIds = useMemo(
    () => ({
      get: () => exceptionIdsState,
      add: (id: number) => setExceptionIdsState(prev => new Set(prev.add(id))),
      remove: (id: number) =>
        setExceptionIdsState(prev => {
          prev.delete(id)
          return new Set(prev)
        }),
      clear: () => setExceptionIdsState(new Set()),
    }),
    [exceptionIdsState],
  )

  useEffect(() => {
    if (selectedAll) {
      const selected = count - exceptionIdsState.size
      if (selected === 0) {
        setIdsCount(t('global.all'))
      } else if (selected < 0) {
        setIdsCount(`${t('global.all')} ${t('global.with_exceptions')}`)
      } else {
        setIdsCount(selected)
      }
    } else {
      const selected = selectedIdsState.size
      if (selected) {
        setIdsCount(selected)
      }
    }
  }, [count, exceptionIdsState.size, selectedAll, selectedIdsState.size, t])

  useEffect(() => {
    if (listing) {
      const ids = listing.items.map(el => el.id)
      ids.forEach(id => setAllContactIds(prev => new Set(prev.add(id))))
      const uniqueIds = ids.filter(id => !selectedIds.get().has(id) && !exceptionIds.get().has(id))
      uniqueIds.forEach(selectedAll ? selectedIds.add : exceptionIds.add)
    }
  }, [listing, selectedAll, exceptionIds, selectedIds])

  const renderSelectAllButton = () => (
    <button
      onClick={() => {
        if (!listing) return
        setSelectedAll(prev => !prev)
        allContactIds.forEach(selectedAll ? exceptionIds.add : selectedIds.add)
        selectedAll ? selectedIds.clear() : exceptionIds.clear()
      }}
      className={`ring-brand-primary flex justify-items-center rounded-full ring-offset-1 focus:outline-hidden focus-visible:ring-2`}
    >
      <CheckCircleSelectedIcon
        isSelected={selectedAll}
        className={`main-transition-colors h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px] ${
          selectedAll && 'fill-green'
        }`}
      />
    </button>
  )

  const renderSelectButton = (item: R) => {
    const selectedItem = selectedIds.get().has(item.id)
    return (
      <button
        onClick={() => {
          if (listing === undefined) return

          if (selectedItem) {
            selectedIds.remove(item.id)
            exceptionIds.add(item.id)
            if (selectedIds.get().size === 0) setSelectedAll(false)
          } else {
            selectedIds.add(item.id)
            exceptionIds.remove(item.id)
          }
        }}
        className={`ring-brand-primary flex justify-items-center rounded-full ring-offset-1 focus:outline-hidden focus-visible:ring-2`}
      >
        <CheckCircleSelectedIcon
          isSelected={selectedItem}
          className={`main-transition-colors h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px] ${
            selectedItem && 'fill-green'
          }`}
        />
      </button>
    )
  }

  const getListingSelect: ListingSelectType = caption => ({
    renderListingSelect: children => (
      <Transition
        show={isMenuShown}
        enter="transition duration-300 ease-in-out transform"
        enterFrom="transform translate-y-full"
        enterTo="transform translate-y-0"
        leave="transition duration-300 ease-in-out transform"
        leaveFrom="transform translate-y-0"
        leaveTo="transform translate-y-full"
        as={Fragment}
        afterLeave={() => {
          selectedIds.clear()
          setSelectedAll(false)
        }}
      >
        <div className="bg-background-secondary border-text-secondary/30 fixed bottom-0 left-0 z-30 flex w-full flex-col gap-3 border-t px-4 py-3 md:flex-row">
          <div className="flex flex-1 items-center justify-center gap-1 ">
            <span className="text-green">{`${caption}:`}</span>
            <span className="text-green font-bold">{idsCount}</span>
          </div>
          {children}
        </div>
      </Transition>
    ),
    closeMenu: () => setIsMenuShown(false),
    selectedAll,
    exceptionIds: exceptionIds.get(),
    selectedIds: selectedIds.get(),
  })

  return { renderSelectAllButton, renderSelectButton, getListingSelect, isMenuShown }
}
